import { defineMessages } from "react-intl";

export const messages = defineMessages({
  buildErrorGeneric: {
    id: "routes.notification.build.error.generic",
    defaultMessage: "Failed to build route.",
    description: "Generic fallback error notification when buildRoute fails",
  },
  buildErrorNoEndEntity: {
    id: "routes.notification.build.error.noEndEntity",
    defaultMessage:
      "Select a company or person for the ending location, or switch it to an address.",
    description: "Build validation error: end location is in record mode but no record is usable",
  },
  buildErrorNoEndGeoPoint: {
    id: "routes.notification.build.error.noEndGeoPoint",
    defaultMessage:
      "Cannot find a location for the given end address. Please pick another address.",
    description: "Build validation error: end address could not be geocoded",
  },
  buildErrorNoStartAddress: {
    id: "routes.notification.build.error.noStartAddress",
    defaultMessage: "Start address is required to build a route.",
    description: "Build validation error: missing start address",
  },
  buildErrorNoStartAt: {
    id: "routes.notification.build.error.noStartAt",
    defaultMessage: "A start date and time is required to build a route.",
    description: "Build validation error: the route has no start date/time",
  },
  buildErrorNoStartEntity: {
    id: "routes.notification.build.error.noStartEntity",
    defaultMessage: "Select a company or person for the starting location.",
    description: "Build validation error: start location is in record mode but no record is usable",
  },
  buildErrorNoStartGeoPoint: {
    id: "routes.notification.build.error.noStartGeoPoint",
    defaultMessage:
      "Cannot find a location for the given start address. Please pick another address.",
    description: "Build validation error: start address could not be geocoded",
  },
  buildErrorNoStops: {
    id: "routes.notification.build.error.noStops",
    defaultMessage: "Add at least one stop to build a route.",
    description: "Build validation error: zero stops",
  },
  buildErrorTooManyStops: {
    id: "routes.notification.build.error.tooManyStops",
    defaultMessage:
      "A route can include at most {max, plural, one {# stop} other {# stops}}. Current route size is {count}.",
    description: "Build validation error: total stop count exceeds MAX_ROUTE_STOPS",
  },
  buildPending: {
    id: "routes.notification.build.pending",
    defaultMessage: "Building…",
    description: "Loading notification shown while buildRoute is in flight",
  },
  createFailure: {
    id: "routes.notification.create.failure",
    defaultMessage: "Couldn't create the route. Please try again.",
    description: "Failure notification when route creation fails",
  },
  createPending: {
    id: "routes.notification.create.pending",
    defaultMessage: "Creating route…",
    description: "Loader notification while creating a route from the editor",
  },
  createSuccess: {
    id: "routes.notification.create.success",
    defaultMessage: "Route created successfully",
    description: "Success notification after the user saves a built route",
  },
  deleteFailure: {
    id: "routes.notification.delete.failure",
    defaultMessage: "Couldn't delete route. Please try again later.",
    description: "Route view sidebar — error toast when route delete fails",
  },
  deletePending: {
    id: "routes.notification.delete.pending",
    defaultMessage: "Deleting route…",
    description: "Route view sidebar — info notification while deleting the route",
  },
  deleteSuccess: {
    id: "routes.notification.delete.success",
    defaultMessage: "Route deleted",
    description: "Route view sidebar — success toast after route delete",
  },
  downloadDataFileTooLarge: {
    id: "routes.map.recordList.exportFiles.dataFileTooLarge.warning",
    defaultMessage:
      "Data {multiple, select, true {files} other {file}} too large to download right now",
    description: "Warning when export exceeds inline-download limit",
  },
  downloadDataFileTooLargeDescription: {
    id: "routes.map.recordList.exportFiles.dataFileTooLarge.warning.description",
    defaultMessage:
      "Your export has been queued for processing and will be sent to your email once finished.",
    description: "Sub-text for the queued-export warning",
  },
  downloadError: {
    id: "routes.map.recordList.exportFiles.error.message",
    defaultMessage: "Download Error",
    description: "Message shown when download fails",
  },
  downloadSuccess: {
    id: "routes.map.recordList.exportFiles.success.message",
    defaultMessage: "Your {multiple, select, true {files have} other {file has}} been downloaded.",
    description: "Message shown when download completes",
  },
  previewUnavailable: {
    id: "routes.notification.preview.unavailable",
    defaultMessage: "This route couldn't be built, so travel details aren't available.",
    description:
      "Route view/edit — error toast shown when opening a route whose preview can't be computed (e.g. no drivable path between the stops)",
  },
  rebuildFailure: {
    id: "routes.view.rebuildPreview.failure",
    defaultMessage: "Failed to re-build the route",
    description: "Route view sidebar — error toast when the preview re-build request fails",
  },
  rebuildPending: {
    id: "routes.view.rebuildPreview.pending",
    defaultMessage: "Re-building route…",
    description: "Route view sidebar — info toast while the remaining route is re-previewed",
  },
  rebuildSuccess: {
    id: "routes.view.rebuildPreview.success",
    defaultMessage: "Route re-built",
    description: "Route view sidebar — success toast after the remaining route is re-previewed",
  },
  resetStopsFailure: {
    id: "routes.notification.resetStops.failure",
    defaultMessage: "Couldn't reset the stops. Please try again later.",
    description: "Route view sidebar — error toast when the reset-all-stops request fails",
  },
  resetStopsNothingToDo: {
    id: "routes.notification.resetStops.nothingToDo",
    defaultMessage: "These stops have already been reset.",
    description:
      "Route view sidebar — info toast when a reset finds no completed stops left, because they were reset elsewhere",
  },
  resetStopsPending: {
    id: "routes.notification.resetStops.pending",
    defaultMessage: "Resetting stops…",
    description: "Route view sidebar — info notification while all stops are being reset",
  },
  resetStopsSuccess: {
    id: "routes.notification.resetStops.success",
    defaultMessage: "Stops reset",
    description: "Route view sidebar — success toast after all stops are reset",
  },
  routeTypeChangedToOrdered: {
    id: "routes.notification.routeType.changedToOrdered",
    defaultMessage: "Route changed to Ordered — your custom stop order will be used",
    description:
      "Info notification shown when manually ordering stops in an Optimized route — dragging, inverting, or inserting one at a chosen position — switches it to Ordered",
  },
  shareFailure: {
    id: "routes.notification.share.failure",
    defaultMessage: "Couldn't update sharing. Please try again later.",
    description: "Route view — error toast when sharing update fails",
  },
  sharePending: {
    id: "routes.notification.share.pending",
    defaultMessage: "Updating sharing…",
    description: "Route view — info notification while a share/unshare is in flight",
  },
  shareSuccess: {
    id: "routes.notification.share.success",
    defaultMessage: "Sharing updated",
    description: "Route view — success toast after sharing updates",
  },
  stopSaving: {
    id: "routes.notification.stop.saving",
    defaultMessage: "Saving…",
    description: "Route view sidebar — info notification while toggling stop checkedOff",
  },
  stopSavingFailure: {
    id: "routes.notification.stop.saving.failure",
    defaultMessage: "Couldn't update stop. Please try again later.",
    description: "Route view sidebar — error toast when stop update fails",
  },
  stopSavingSuccess: {
    id: "routes.notification.stop.saving.success",
    defaultMessage: "Stop updated",
    description: "Route view sidebar — success toast after stop update",
  },
  updateFailure: {
    id: "routes.notification.update.failure",
    defaultMessage: "Couldn't update route. Please try again later.",
    description: "Route view sidebar — error toast when route update fails",
  },
  updatePending: {
    id: "routes.notification.update.pending",
    defaultMessage: "Saving…",
    description: "Route view sidebar — info notification for non-preview edits",
  },
  updateRebuildPending: {
    id: "routes.notification.update.rebuildPending",
    defaultMessage: "Building route…",
    description: "Route view sidebar — info notification while route is being rebuilt",
  },
  updateSuccess: {
    id: "routes.notification.update.success",
    defaultMessage: "Route updated",
    description: "Route view sidebar — success toast after route update",
  },
});
